import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  getUserId,
  getSessionData,
} from "./localStorageService";
import { isTokenExpiringSoon } from "./tokenUtils";
import { SelfUserClassificationEnum } from "../../shared/constants/constants";
import { getApiBaseUrl } from "../../shared/config/tenantConfiguration";
import {
  getBackendClientHeaders,
  getWebDeviceId,
  WEB_APP_CHANNEL,
} from "../deviceIdentity";

const axiosInstance = axios.create({
  headers: { "Content-Type": "application/json" },
});

// ─── helpers for default headers ────────────────────────────
const getClientPlatform = () => {
  if (typeof navigator === "undefined") return "Chrome";
  const userAgent = navigator.userAgent || "";
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iOS";
  return "Chrome";
};

let ipAddressCache = null;
let ipAddressPromise = null;
let ipAddressFetchedAt = 0;
const IP_CACHE_TTL_MS = 5 * 60 * 1000;

const getIpAddress = async () => {
  const now = Date.now();
  if (ipAddressCache && now - ipAddressFetchedAt < IP_CACHE_TTL_MS) return ipAddressCache;
  if (ipAddressPromise) return ipAddressPromise;

  ipAddressPromise = (async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      if (!response.ok) throw new Error("Failed to fetch IP");
      const data = await response.json();
      ipAddressCache = data?.ip || null;
      ipAddressFetchedAt = Date.now();
      return ipAddressCache;
    } catch (error) {
      console.warn("Unable to resolve client IP address:", error);
      return null;
    } finally {
      ipAddressPromise = null;
    }
  })();

  return ipAddressPromise;
};

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    ipAddressCache = null;
    ipAddressFetchedAt = 0;
  });
}

// ─── token refresh logic ─────────────────────────────────────
let isRefreshing = false;
let refreshPromise = null;

const refreshAccessToken = async () => {
  if (isRefreshing && refreshPromise) {
    console.log("🔄 Token refresh already in progress, waiting...");
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();
      const userId = getUserId();
      if (!refreshToken) {
        console.error("❌ No refresh token available");
        return null;
      }

      console.log("🔄 Refreshing access token proactively...");
      const response = await axios.post(
        `${getApiBaseUrl()}/api/bpm/cmd`,
        {
          cmd: "LoginRefreshTokenCommand",
          data: JSON.stringify({ userId, refreshToken }),
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...getBackendClientHeaders(),
          },
        }
      );

      const newAccessToken = response?.data?.outData?.accessToken;
      if (newAccessToken) {
        console.log("✅ Token refreshed successfully");
        setAccessToken(newAccessToken);
        return newAccessToken;
      } else {
        console.error("❌ Token refresh failed: No access token in response");
        return null;
      }
    } catch (error) {
      console.error("❌ Token refresh error:", error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// ─── Request interceptor ─────────────────────────────────────
axiosInstance.interceptors.request.use(
  async (config) => {
    config.baseURL = getApiBaseUrl();
    const accessToken = getAccessToken();

    config.headers = config.headers || {};

    // This is the web client even when it is opened from a mobile browser.
    config.headers["X-App-Channel"] = WEB_APP_CHANNEL;
    config.headers["X-Device-Id"] = getWebDeviceId();
    if (!config.headers["X-Client-Platform"]) {
      config.headers["X-Client-Platform"] = getClientPlatform();
    }
    if (!config.headers["X-Ip-Address"]) {
      const ipAddress = await getIpAddress();
      if (ipAddress) {
        config.headers["X-Ip-Address"] = ipAddress;
      }
    }

    // Skip token refresh for the refresh token request itself
    if (config.url?.includes("LoginRefreshTokenCommand")) {
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    }

    if (accessToken) {
      if (isTokenExpiringSoon(accessToken, 1)) {
        console.log("⏰ Token expiring soon, refreshing proactively...");
        const newToken = await refreshAccessToken();
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`;
          console.log("✅ Request will use refreshed token");
        } else {
          console.warn("⚠️ Token refresh failed, using existing token");
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } else {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response interceptor ────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const refreshToken = getRefreshToken();
    const originalRequest = error?.config;
    const sessionData = getSessionData();
    const classification = sessionData?.userClassification;

    if (error?.response?.status === 401 && !refreshToken) {
      console.log("❌ No refresh token available, redirecting to login");
      localStorage.clear();
      localStorage.setItem("isLoggedIn", false);
      localStorage.setItem("logoutEvent", Date.now());
      const loginPath =
        classification === SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
          ? "/authentication/admin/login"
          : "/authentication/login";
      window.location.href = loginPath;
      return Promise.reject(error);
    }

    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log("⚠️ 401 error despite proactive refresh, trying one more time...");
      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } else {
          console.error("❌ Token refresh failed, redirecting to login");
          localStorage.clear();
          localStorage.setItem("isLoggedIn", false);
          localStorage.setItem("logoutEvent", Date.now());
          const loginPath =
            classification === SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
              ? "/authentication/admin/login"
              : "/authentication/login";
          window.location.href = loginPath;
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error("❌ Refresh error, redirecting to login");
        localStorage.clear();
        localStorage.setItem("isLoggedIn", false);
        localStorage.setItem("logoutEvent", Date.now());
        const loginPath =
          classification === SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
            ? "/authentication/admin/login"
            : "/authentication/login";
        window.location.href = loginPath;
        return Promise.reject(refreshError);
      }
    }

    if (error?.response?.status === 401) {
      console.error("❌ Still 401 after retry, redirecting to login");
      localStorage.clear();
      localStorage.setItem("isLoggedIn", false);
      localStorage.setItem("logoutEvent", Date.now());
      const loginPath =
        classification === SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
          ? "/authentication/admin/login"
          : "/authentication/login";
      window.location.href = loginPath;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
